import { useEffect, useState } from "react";
import queryString from "query-string";

import utils from "../utils";

/**
 * Row selection for a paginated intents list.
 *
 * The table only ever holds one page, so on its own the select-all checkbox can
 * only pick that page's ids -- and a bulk delete then silently spared every
 * other page. `allMatching` is the mode that checkbox switches into instead:
 * the selection is "every row the current filters match", and bulk actions
 * send those filters rather than an id list.
 *
 * `filters` are the params the list is loaded with (`page` is ignored);
 * `total` is the list's pagination count.
 *
 * `scope` is what a bulk endpoint needs: `qs` goes on the URL, `body` into the
 * request body, `count` into the confirm / success messages.
 *
 * `explicitIds` is the selection as concrete ids, for actions that can't take a
 * filter (merge): `pageIds` (the loaded page) stand in for "all matching" when
 * that page is the whole result, and it is null when the selection reaches
 * past what is loaded.
 */
export const useIntentSelection = ({ filters, total, pageIds }) => {
  const [selectedIds, setSelectedIds] = useState([]);
  const [allMatching, setAllMatching] = useState(false);

  const clear = () => {
    setSelectedIds([]);
    setAllMatching(false);
  };

  // A selection made against one result set must not carry into another:
  // "all matching" would quietly re-target to the new filters. Paging keeps it.
  const { page, ...query } = filters || {};
  const queryKey = JSON.stringify(utils.cleanEmpty(query));
  useEffect(() => clear(), [queryKey]);

  const count = allMatching ? total ?? selectedIds.length : selectedIds.length;

  const scope = allMatching
    ? {
        count,
        qs: `?${queryString.stringify(utils.cleanEmpty(query))}`,
        body: { all_matching: true },
      }
    : { count, qs: "", body: { ids: selectedIds } };

  // The header checkbox always switches to "all matching", even when every
  // match fits on the page on screen.
  const explicitIds = !allMatching
    ? selectedIds
    : pageIds && total != null && total <= pageIds.length
      ? pageIds
      : null;

  return {
    selectedIds,
    setSelectedIds,
    allMatching,
    setAllMatching,
    count,
    scope,
    explicitIds,
    clear,
  };
};
